//* Importing libries from React
import React, { useMemo, useState, useEffect } from "react";

//* Importing components from react-bootstrap
import {
  Table,
  InputGroup,
  FormControl,
  Badge,
  Row,
  Col,
  Button,
  Form,
} from "react-bootstrap";

//*Importing icons from lucide-react
import { Filter, User, AlertCircle } from "lucide-react";

//*Importing hooks from react-router
import { Link } from "react-router-dom";

//*Importing types from types
import { type Student } from "../../../hooks/units/types/types";
import { type Pagination } from "../../../services/stores/types";

//* Importar o debounce do lodash
var debounce = require("lodash.debounce");

//* types for props
type StudentsTabProps = {
  studentsData: {
    data: Student[];
    pagination: Pagination;
  };
  totalStudents: number;
  totalActiveStudents: number;
  studentFilters: {
    searchTermStudents: string;
    studentFilter: "all" | "active" | "inactive";
    pageStudents: number;
    perPageStudents: number;
  };
  setStudentFilters: React.Dispatch<
    React.SetStateAction<{
      searchTermStudents: string;
      studentFilter: "all" | "active" | "inactive";
      pageStudents: number;
      perPageStudents: number;
    }>
  >;
  reloadData: () => void;
};

export const StudentsTab: React.FC<StudentsTabProps> = ({
  studentsData,
  totalStudents,
  totalActiveStudents,
  studentFilters,
  setStudentFilters,
  reloadData,
}) => {
  const { data: students, pagination } = studentsData;

  // Estado local para o valor do input de pesquisa
  const [searchTerm, setSearchTerm] = useState(
    studentFilters.searchTermStudents
  );

  // Função debounced para atualizar os filtros
  const debouncedSetFilters = useMemo(
    () =>
      debounce((value: string) => {
        setStudentFilters((prev) => ({
          ...prev,
          searchTermStudents: value,
          pageStudents: 1,
        }));
      }, 500), // 500ms de debounce
    [setStudentFilters]
  );

  // Atualiza o estado local do input e aplica o debounce nos filtros
  const handleSearchStudents = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value); // Atualiza o valor do input imediatamente
    debouncedSetFilters(value); // Aplica debounce na atualização dos filtros
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setStudentFilters((prev) => ({
      ...prev,
      studentFilter: event.target.value as "all" | "active" | "inactive",
      pageStudents: 1,
    }));
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setStudentFilters((prev) => ({
        ...prev,
        pageStudents: newPage,
      }));
    }
  };

  // Limpa o debounce quando o componente é desmontado
  useEffect(() => {
    return () => {
      debouncedSetFilters.cancel();
    };
  }, [debouncedSetFilters]);

  return (
    <>
      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text>
              <Filter size={16} />
            </InputGroup.Text>
            <FormControl
              placeholder="Pesquisar alunos..."
              value={searchTerm}
              onChange={handleSearchStudents}
              aria-label="Pesquisar alunos"
            />
          </InputGroup>
        </Col>
        <Col md={6}>
          <Form.Select
            value={studentFilters.studentFilter}
            onChange={handleFilterChange}
            aria-label="Filtrar por status"
          >
            <option value="all">Todos</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </Form.Select>
        </Col>
      </Row>

      {students.length === 0 ? (
        <div className="text-center text-muted py-5">
          <AlertCircle size={48} className="mb-3" />
          <p>Nenhum aluno encontrado.</p>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table className="table-hover align-middle">
              <thead className="bg-primary text-white">
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student: Student) => (
                  <tr key={student.studentId}>
                    <td>
                      <div className="d-flex align-items-center">
                        <User
                          className="me-2 text-primary"
                          size={16}
                          aria-hidden="true"
                        />
                        <span className="fw-bold">{student.name}</span>
                      </div>
                    </td>
                    <td>{student.email}</td>
                    <td>
                      {student.is_active ? (
                        <Badge bg="primary">Ativo</Badge>
                      ) : (
                        <Badge bg="secondary">Inativo</Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        as={Link as any}
                        to={`/users-student`}
                      >
                        Gerenciar Aluno
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Controles de Paginação */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              Página {pagination.currentPage} de {pagination.totalPages}
            </div>
            <div>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={pagination.currentPage === 1}
                onClick={() => handlePageChange(pagination.currentPage - 1)}
              >
                Anterior
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={pagination.currentPage === pagination.totalPages}
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                className="ms-2"
              >
                Próximo
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
